import {
	useGetAccountInsightsSessionActivityQuery,
	useLazyGetAccountInsightsSessionActivityQuery,
} from "@api/accountManagement";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	CHART_COLORS,
	createGradient,
	formatDateLabel,
	formatDurationTooltip,
	getBaseChartOptions,
} from "../../utils";

const SESSION_FETCH_LIMIT = 50;

const isElement = (node) => node instanceof Element;

const useSessionInfiniteScroll = ({
	onLoadMore,
	hasMore,
	loading,
	enabled = true,
	watchKey,
}) => {
	const scrollRootRef = useRef(null);
	const sentinelRef = useRef(null);
	const isFetchingRef = useRef(false);
	const [scrollRootElement, setScrollRootElement] = useState(null);
	const [sentinelElement, setSentinelElement] = useState(null);

	const setScrollRootRef = useCallback((node) => {
		scrollRootRef.current = node;
		setScrollRootElement(isElement(node) ? node : null);
	}, []);

	const setSentinelRef = useCallback((node) => {
		sentinelRef.current = node;
		setSentinelElement(isElement(node) ? node : null);
	}, []);

	const tryLoadMore = useCallback(async () => {
		if (!enabled || !hasMore || loading || isFetchingRef.current) {
			return;
		}

		isFetchingRef.current = true;

		try {
			await onLoadMore();
		} finally {
			isFetchingRef.current = false;
		}
	}, [enabled, hasMore, loading, onLoadMore]);

	const isSentinelVisible = useCallback(() => {
		const root = scrollRootRef.current;
		const sentinel = sentinelRef.current;

		if (!isElement(root) || !isElement(sentinel)) {
			return false;
		}

		const rootRect = root.getBoundingClientRect();
		const sentinelRect = sentinel.getBoundingClientRect();

		return sentinelRect.top <= rootRect.bottom + 48;
	}, []);

	useEffect(() => {
		if (!enabled || !hasMore || !scrollRootElement || !sentinelElement) {
			return undefined;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					tryLoadMore();
				}
			},
			{
				root: scrollRootElement,
				rootMargin: "48px",
				threshold: 0,
			}
		);

		observer.observe(sentinelElement);

		return () => observer.disconnect();
	}, [enabled, hasMore, scrollRootElement, sentinelElement, tryLoadMore]);

	useEffect(() => {
		if (!enabled || !hasMore || loading) {
			return;
		}

		if (isSentinelVisible()) {
			tryLoadMore();
		}
	}, [enabled, hasMore, loading, isSentinelVisible, tryLoadMore, watchKey]);

	const scrollToTop = useCallback(() => {
		if (isElement(scrollRootRef.current)) {
			scrollRootRef.current.scrollTo({ top: 0 });
		}
	}, []);

	return {
		scrollRootRef: setScrollRootRef,
		sentinelRef: setSentinelRef,
		scrollToTop,
	};
};

const useController = ({ translate }) => {
	const [dateRange, setDateRange] = useState([moment().subtract(30, "days"), moment()]);
	const [interval, setInterval] = useState("day");
	const [sessions, setSessions] = useState([]);
	const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const queryParams = useMemo(
		() => ({
			from: dateRange[0].valueOf(),
			to: dateRange[1].valueOf(),
			interval,
			limit: SESSION_FETCH_LIMIT,
		}),
		[dateRange, interval]
	);

	const listKey = `${queryParams.from}-${queryParams.to}-${queryParams.interval}`;

	const sessionQuery = useGetAccountInsightsSessionActivityQuery(queryParams, {
		refetchOnMountOrArgChange: true,
	});

	const [fetchMoreSessions] = useLazyGetAccountInsightsSessionActivityQuery();

	useEffect(() => {
		setSessions([]);
		setLastEvaluatedKey(null);
	}, [listKey]);

	useEffect(() => {
		if (!sessionQuery.data) {
			return;
		}

		setSessions(sessionQuery.data.sessions || []);
		setLastEvaluatedKey(sessionQuery.data.lastEvaluatedKey ?? null);
	}, [sessionQuery.data]);

	const handleLoadMore = useCallback(() => {
		if (!lastEvaluatedKey) {
			return Promise.resolve({ added: 0, hasMore: false });
		}

		setIsLoadingMore(true);

		return fetchMoreSessions({
			...queryParams,
			startKey: lastEvaluatedKey,
		})
			.unwrap()
			.then((response) => {
				const addedSessions = response.sessions || [];

				setSessions((prev) => [...prev, ...addedSessions]);
				setLastEvaluatedKey(response.lastEvaluatedKey ?? null);

				return {
					added: addedSessions.length,
					hasMore: Boolean(response.lastEvaluatedKey),
				};
			})
			.finally(() => {
				setIsLoadingMore(false);
			});
	}, [fetchMoreSessions, lastEvaluatedKey, queryParams]);

	const hasMore = Boolean(lastEvaluatedKey);
	const tableLoading = isLoadingMore || sessionQuery.isFetching;

	const infiniteScroll = useSessionInfiniteScroll({
		onLoadMore: handleLoadMore,
		hasMore,
		loading: tableLoading,
		enabled: sessions.length > 0,
		watchKey: sessions.length,
	});

	useEffect(() => {
		infiniteScroll.scrollToTop();
	}, [listKey, infiniteScroll.scrollToTop]);

	const buckets = sessionQuery.data?.buckets || [];

	const durationChart = useMemo(
		() => ({
			data: {
				labels: buckets.map((bucket) => formatDateLabel(bucket.date)),
				datasets: [
					{
						label: translate("accountInsights.sessionActivity.chartLabel"),
						data: buckets.map((bucket) =>
							Math.round((Number(bucket.durationSeconds) || 0) / 60)
						),
						borderColor: CHART_COLORS.primary,
						backgroundColor: (context) => {
							const { chart } = context;
							return createGradient(chart.ctx, chart.chartArea, [
								{ offset: 0, color: CHART_COLORS.primaryFill },
								{ offset: 1, color: "rgba(99, 179, 237, 0.02)" },
							]);
						},
						fill: true,
						tension: 0.35,
						pointRadius: 4,
						pointHoverRadius: 6,
						pointBackgroundColor: CHART_COLORS.primary,
						pointBorderColor: "#1a1d2e",
						pointBorderWidth: 2,
						borderWidth: 2,
					},
				],
			},
			options: {
				...getBaseChartOptions(),
				plugins: {
					...getBaseChartOptions().plugins,
					tooltip: {
						...getBaseChartOptions().plugins.tooltip,
						callbacks: {
							label: (context) => formatDurationTooltip(context.parsed.y),
						},
					},
				},
				scales: {
					...getBaseChartOptions().scales,
					y: {
						...getBaseChartOptions().scales.y,
						title: {
							display: true,
							text: translate("accountInsights.charts.minutes"),
							color: CHART_COLORS.text,
							font: { size: 10 },
						},
					},
				},
			},
		}),
		[buckets, translate]
	);

	const getEndReasonLabel = useCallback(
		(endReason) => {
			if (!endReason) {
				return "—";
			}

			const key = `accountInsights.endReason.${endReason}`;
			const translated = translate(key);

			return translated === key ? endReason : translated;
		},
		[translate]
	);

	const tableData = useMemo(
		() =>
			sessions.map((session, index) => ({
				...session,
				key: `${session.sessionId}-${session.startTime}-${index}`,
			})),
		[sessions]
	);

	return {
		state: {
			buckets,
			isLoading: sessionQuery.isLoading,
			isError: sessionQuery.isError,
			durationChart,
			sessions,
			tableData,
			hasMore,
			tableLoading,
			showSentinel: hasMore || tableLoading,
			interval,
		},
		handlers: {
			refetch: sessionQuery.refetch,
			setDateRange,
			setInterval,
			getEndReasonLabel,
		},
		infiniteScroll,
	};
};

export default useController;
